import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import ReactDOM from "react-dom";
import { ModalHeader, Modal, ModalBody } from "reactstrap";
import { useResponsive } from "../Hooks/useResponsive";
import styles from "./Modal.module.scss";

export type ModalDismissReason = "backdrop" | "escape";

export interface ModalDialogProps {
  visible: boolean;
  toggle?: (event?: SyntheticEvent) => void;
  onRequestClose?: (reason: ModalDismissReason) => void;
  style?: CSSProperties;
  className?: string;
  bodyClass?: string;
  dialogClassName?: string;
  modalClassName?: string;
  contentClassName?: string;
  backdropClassName?: string;
  size?: string;
  title?: ReactNode;
  subTitle?: ReactNode;
  pbody?: ReactNode;
  children?: ReactNode;
  keyboard?: boolean;
  backdrop?: boolean | "static";
  centered?: boolean;
  fullscreen?: boolean | "sm" | "md" | "lg" | "xl";
  labelledBy?: string;
  roundedBorder?: boolean;
  /** On phones the dialog slides up from the bottom as a sheet instead of sitting centred. */
  bottomSheetOnMobile?: boolean;
}

/** Class names that turn a reactstrap modal into a bottom sheet, for modals that don't go through ModalDialog. */
export const bottomSheetClassNames = {
  modalClassName: styles.sheetModal,
  dialogClassName: styles.sheetDialog,
  contentClassName: styles.sheetContent,
};

function DialogBody({ visible, toggle, style, className = "", onRequestClose, sheet = false, ...props }: ModalDialogProps & { sheet?: boolean }) {
  // props.roundedBorder = true: gives the borderRadius of 1.5rem

  const handleToggle = (event?: SyntheticEvent): void => {
    if (onRequestClose) {
      const keyboardEvent = event as unknown as { key?: string; type?: string };
      onRequestClose(keyboardEvent?.key === "Escape" || keyboardEvent?.type === "keydown" ? "escape" : "backdrop");
      return;
    }
    toggle?.(event);
  };

  return <div className={`${styles.ModalDialog}`}>
    <Modal
      toggle={handleToggle}
      isOpen={visible}
      style={style}
      centered={props.centered ?? true}
      size={props.size ? props.size : "lg"}
      className={`${props.dialogClassName ?? ""} ${sheet ? styles.sheetDialog : ""}`}
      fade={false}
      keyboard={props.keyboard}
      backdrop={props.backdrop}
      fullscreen={props.fullscreen}
      labelledBy={props.labelledBy}
      modalClassName={`${props.modalClassName ?? ""} ${sheet ? styles.sheetModal : ""}`}
      backdropClassName={props.backdropClassName}
      contentClassName={`${styles.roundedBorder} ${props.contentClassName ?? ""} ${sheet ? styles.sheetContent : ""}`}
    >
      {(props.title || props.subTitle) &&
        <ModalHeader className={`${styles.header} ${className == 'member-popup' ? 'memHeader' : ''} ${className}`} toggle={handleToggle} style={{
          borderBottom: "none"
        }}>

          {props.title}
          <div className={styles.subTitle}>
            {props.subTitle}
          </div>

        </ModalHeader>}
      {props.pbody && (props.pbody)}
      <ModalBody className={`${styles.body} ${props.bodyClass ? props.bodyClass : ''} ${className}`}>
        {props.children}
      </ModalBody>
    </Modal>
  </div >
}

// DialogBody is called as a plain function only while visible, so the hook has to live here.
const ModalDialog = (props: ModalDialogProps): JSX.Element | null => {
  const { isMobile } = useResponsive();
  return props.visible
    ? ReactDOM.createPortal(DialogBody({ ...props, sheet: Boolean(props.bottomSheetOnMobile) && isMobile }), document.body)
    : null;
};

export default ModalDialog;
